import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import useAuth from '../../shared/hooks/useAuth';
import FirstAccess from './components/FirstAccess';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import { Img, TextResetPassWord, Views, Wrapper } from './styles';

const BETA_TESTER_EMAILS = [
  'matrixprint@matrixprint.com.br',
  'mialich@cartazimpresso.com.br',
  'CAROLINAFAVETTA@GMAIL.COM'
];

const BetaModal = ({ onContinue }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleContinue = () => {
    setVisible(false);
    setTimeout(() => onContinue(), 350);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 10, 30, 0.82)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.35s ease',
        cursor: 'default'
      }}
    >
      <div
        style={{
          background: 'linear-gradient(145deg, #0a1628 0%, #0d2044 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
          padding: '48px 40px 36px',
          maxWidth: 420,
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,180,255,0.1)',
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
          transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          position: 'relative',
          cursor: 'auto'
        }}
      >
        <span
          style={{
            fontSize: 52,
            marginBottom: 8,
            display: 'block',
            filter: 'drop-shadow(0 0 18px rgba(0,180,255,0.5))'
          }}
        >
          🚀
        </span>

        <div
          style={{
            display: 'inline-block',
            background: 'linear-gradient(90deg, #00b4ff, #0066ff)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            padding: '4px 14px',
            borderRadius: 99,
            marginBottom: 20,
            fontFamily: 'monospace'
          }}
        >
          Beta Tester
        </div>

        <p
          style={{
            color: '#fff',
            fontSize: 22,
            fontWeight: 700,
            marginBottom: 12,
            lineHeight: 1.35,
            fontFamily: 'sans-serif'
          }}
        >
          Você foi selecionado para testar nosso novo sistema! Estamos lançando uma versão beta
          exclusiva para um grupo seleto de usuários, e você é um deles.
        </p>

        <p
          style={{
            color: 'rgba(255,255,255,0.65)',
            fontSize: 14.5,
            lineHeight: 1.7,
            marginBottom: 28,
            fontFamily: 'sans-serif'
          }}
        >
          Mais rápido, limpo e performático — experimente agora antes de todo mundo.
        </p>

        <a
          href="https://drv82zvny7vue.cloudfront.net/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            width: '100%',
            padding: '14px 24px',
            background: 'linear-gradient(90deg, #00b4ff 0%, #0055ff 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 15,
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
            letterSpacing: '0.03em',
            boxShadow: '0 6px 24px rgba(0,100,255,0.4)',
            marginBottom: 12,
            fontFamily: 'sans-serif',
            textDecoration: 'none',
            boxSizing: 'border-box'
          }}
        >
          Acessar novo sistema →
        </a>

        <button
          type="button"
          onClick={handleContinue}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.35)',
            fontSize: 13,
            cursor: 'pointer',
            fontFamily: 'sans-serif',
            textDecoration: 'underline',
            padding: 4,
            display: 'block',
            margin: '0 auto'
          }}
        >
          Continuar aqui por enquanto
        </button>
      </div>
    </div>
  );
};

BetaModal.propTypes = {
  onContinue: PropTypes.func.isRequired
};

const Authentication = () => {
  const auth = useAuth();
  const { resetStatusPassword } = useAuth();
  const [resetPasswordState, setResetPasswordStatus] = useState(false);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const loginWrapperRef = useRef(null);

  // função de submit real do form
  const pendingSubmitFnRef = useRef(null);

  // ✅ bypass: quando true, o próximo submit NÃO valida beta
  const skipBetaOnceRef = useRef(false);

  useEffect(() => {
    setResetPasswordStatus(false);
    resetStatusPassword();
  }, [auth.resetPasswordStatus]);

  useEffect(() => {
    const wrapper = loginWrapperRef.current;
    if (!wrapper) return;

    const findEmailValue = () => {
      const emailInput = wrapper.querySelector(
        'input[type="email"], input[name="email"], input[name="username"], input[name="login"]'
      );
      return emailInput?.value?.trim?.().toLowerCase?.() || '';
    };

    const handleSubmitCapture = (e) => {
      const form = e.target?.closest?.('form');
      if (!form) return;

      // ✅ se o usuário escolheu "Continuar aqui", deixa logar e não valida
      if (skipBetaOnceRef.current) {
        skipBetaOnceRef.current = false; // só 1 vez
        return; // não bloqueia
      }

      const email = findEmailValue();

      if (BETA_TESTER_EMAILS.includes(email)) {
        e.preventDefault();
        e.stopPropagation();

        pendingSubmitFnRef.current = () => {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        };

        setShowBetaModal(true);
      }
    };

    wrapper.addEventListener('submit', handleSubmitCapture, true);
    return () => wrapper.removeEventListener('submit', handleSubmitCapture, true);
  }, []);

  const handleContinue = () => {
    // ✅ marca bypass antes de submeter novamente
    skipBetaOnceRef.current = true;

    setShowBetaModal(false);

    const fn = pendingSubmitFnRef.current;
    pendingSubmitFnRef.current = null;

    if (fn) {
      setTimeout(() => {
        fn(); // agora vai passar direto sem abrir modal
      }, 400);
    }
  };

  return (
    <Wrapper>
      {showBetaModal && <BetaModal onContinue={handleContinue} />}

      <div
        style={{
          cursor: 'pointer',
          padding: 10,
          backgroundColor: '#00224d',
          flex: 1,
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        {resetPasswordState === true && (
          <div
            onClick={() => setResetPasswordStatus(false)}
            style={{
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              position: 'relative',
              right: 130,
              bottom: 50
            }}
          >
            <div
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                display: 'flex'
              }}
            >
              <ArrowBackIcon style={{ color: '#fff', fontSize: 25 }} />
              <span style={{ color: '#fff', marginLeft: 6, fontSize: 22 }}>Voltar</span>
            </div>
          </div>
        )}

        <div
          style={{
            marginBottom: 30,
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            display: 'flex'
          }}
        >
          <img src="/img/footer-logo.png" alt="Logo" />
        </div>

        <div style={{ flexDirection: 'column', display: 'flex', justifyContent: 'flex-start' }}>
          {!auth.isAuthenticated && resetPasswordState === false && (
            <div ref={loginWrapperRef}>
              <Login />
            </div>
          )}

          {resetPasswordState === true && <ResetPassword />}

          {auth.user?.userStatus === 'FIRST_ACCESS' && <FirstAccess />}

          {auth.user?.userStatus !== 'FIRST_ACCESS' && resetPasswordState === false && (
            <div style={{ marginTop: 20 }} onClick={() => setResetPasswordStatus(true)}>
              <TextResetPassWord>Esqueceu sua senha?</TextResetPassWord>
            </div>
          )}
        </div>
      </div>

      <Views>
        <Img src="/img/cartaz-personalizado.png" />
      </Views>

      <div />
    </Wrapper>
  );
};

export default Authentication;
